import React from "react";
import { motion } from "framer-motion";
import { InstagramLogo, FacebookLogo, LinkedinLogo, ArrowUpRight, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { Logo, scrollToId } from "./Logo";
import { NAV } from "../data/site";
import { useLead } from "../context/LeadContext";

const SOCIALS = [
  { Icon: InstagramLogo, label: "Instagram", href: "https://www.instagram.com/jakincdm/" },
  { Icon: FacebookLogo, label: "Facebook", href: "https://www.facebook.com/JakinCDM/" },
  { Icon: LinkedinLogo, label: "LinkedIn", href: "https://www.linkedin.com/in/stefanie-jakincdm" },
];

const EMAIL = "jakincdm@gmail.com";
const PHONE = "+1 941-232-5985";

export const Footer = () => {
  const { openLead } = useLead();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goSection = (id) => (pathname !== "/" ? navigate(`/#${id}`) : scrollToId(id));
  return (
    <footer id="contact" className="relative overflow-hidden bg-ink pt-20 text-white">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-12 border-b border-white/10 pb-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo dark />
            <p className="mt-6 max-w-sm font-sans text-sm leading-relaxed text-zinc-400">
              Website Design | Branding | Social Media | Marketing Strategy
            </p>
            <button
              onClick={() => openLead("consultation")} data-testid="footer-contact-button"
              className="group mt-8 flex items-center gap-3 bg-teal px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-ink"
            >
              Contact Us <ArrowUpRight weight="bold" className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Navigate</h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button onClick={() => goSection(n.id)} className="font-display text-lg font-medium text-zinc-300 transition-colors hover:text-teal" data-testid={`footer-nav-${n.id}`}>
                    {n.label}
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => navigate("/terms")} className="font-display text-lg font-medium text-zinc-300 transition-colors hover:text-teal" data-testid="footer-nav-terms">
                  Terms & Agreement
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Get in touch</h4>
            <ul className="space-y-4">
              <li>
                <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="group flex items-center gap-3 font-sans text-sm text-zinc-300 transition-colors hover:text-teal">
                  <EnvelopeSimple size={18} weight="bold" className="text-teal" /> {EMAIL}
                </a>
              </li>
              <li>
                <a href={`tel:${PHONE.replace(/[^+\d]/g, "")}`} data-testid="footer-phone" className="group flex items-center gap-3 font-sans text-sm text-zinc-300 transition-colors hover:text-teal">
                  <Phone size={18} weight="bold" className="text-teal" /> {PHONE}
                </a>
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`social-${label.toLowerCase()}`}
                  className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-300 transition-colors hover:border-teal hover:bg-teal hover:text-white"
                >
                  <Icon size={20} weight="fill" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="py-8 text-center md:flex md:items-center md:justify-between md:text-left">
          <p className="font-display text-sm font-medium tracking-tight text-zinc-400">
            © {new Date().getFullYear()} Jakin Creative Design & Marketing. All rights reserved.
          </p>
          <p className="mt-2 font-sans text-xs uppercase tracking-wider text-zinc-600 md:mt-0">We Establish.</p>
        </div>
      </div>

      <div className="select-none overflow-hidden">
        <div className="whitespace-nowrap px-5 pb-4 text-center font-display text-[18vw] font-black leading-[0.8] tracking-tighter text-white/[0.04]">
          JAKIN CREATIVE
        </div>
      </div>
    </footer>
  );
};
